/**
 * @author Hadeer Elbanna
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-10-25 11:58:40
 * @description Implementation of the external navbar react component
 * @filename  external-navbar.tsx
 */
import React from "react";
import ProgressiveImage from "react-progressive-image";
import { fetchQuery } from "react-relay";
import LazyLoad from "react-lazyload";
import { PreviewKind } from "@radivision/graphql";
import { ENVIRONMENT } from "../../../relay/relay-environment";
import { Router } from "../../../utilities/router";
import { Preview } from "../../../utilities/preview";
import { POSTER_QUERY } from "../../../relay/queries/poster-query";

const LOGO = require("../../../../static/logo-big.png");
const DEFAULT_IMAGE: string = require("../../../../static/GrayColor.jpg");

interface ExternalHeroProps {
  /**
   * poster id
   *
   * @type {{ id: string }}
   * @memberof ExternalHeroProps
   */
  poster?: { id: string };
}

/**
 * The state of the ExternalHero component.
 *
 * @interface
 */
interface ExternalHeroState {
  /**
   * The list graph result.
   *
   * @type {any}
   */
  graphResult?: any;
}

/**
 *
 * A React component that renders the external navbar for about us.
 *
 * @extends {React.Component}
 */
export class ExternalHero extends React.Component<
  ExternalHeroProps,
  ExternalHeroState
> {
  /**
   * Constructor.
   *
   * @param { ExternalHeroProps} props The props of the component.
   */
  constructor(props: ExternalHeroProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    if (this.props.poster !== undefined && this.props.poster !== null) {
      const id = this.props.poster.id;
      if (id === undefined || id === null) {
        return (
          <div className="alert alert-danger about-danger">
            Error Loading External Hero Details!
          </div>
        );
      }
      fetchQuery(ENVIRONMENT, POSTER_QUERY, {
        id,
      }).then((queryResults: any) => {
        if (queryResults.poster.hasErrors) {
          Router.to("home");
        }
        if (queryResults.poster && !queryResults.poster.hasErrors) {
          const RESULT = queryResults.poster.poster;
          this.setState({
            graphResult: RESULT,
          });
        }
      });
    }
  }
  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div>
        {this.state.graphResult ? (
          <div className="external cover-image">
            <LazyLoad once={true}>
              <ProgressiveImage
                src={`${Preview.getUrlFromPreview(
                  this.state.graphResult.previews,
                  [PreviewKind.HERO, PreviewKind.BACKGROUND]
                )}`}
                placeholder={DEFAULT_IMAGE}
              >
                {(src: any, LOADING: any, srcSetData: any) => (
                  <img
                    loading="lazy"
                    alt="About us"
                    src={src}
                    onError={(event) => {
                      DEFAULT_IMAGE;
                    }}
                  />
                )}
              </ProgressiveImage>
              <img src={LOGO} alt="Radivision logo" className="image-logo" />
            </LazyLoad>
          </div>
        ) : (
          <ProgressiveImage src={DEFAULT_IMAGE} placeholder={DEFAULT_IMAGE}>
            {(src: any) => <img loading="lazy" alt="About us" src={src} />}
          </ProgressiveImage>
        )}
      </div>
    );
  }
}
